import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Mail, Phone, Send, User, AtSign, Hash, MessageSquare, ChevronDown, Sparkles, Check } from 'lucide-react';

const countries = [
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
  { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33' },
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰', dialCode: '+92' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪', dialCode: '+971' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺', dialCode: '+7' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '+27' },
];

const ContactSection = () => {
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef();

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1, duration: 0.4 } } };
  const itemVariants = { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } } };
  const formVariants = { hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Get form data
    const formData = {
      name: e.target.name.value,
      email: e.target.email.value,
      message: e.target.message.value,
      phone: e.target.phone?.value || '',
      zip: e.target.zip?.value || ''
    };

    console.log('📤 Sending form data:', formData);

    try {
      // Call backend API
      console.log('🔗 Calling API: http://localhost:5000/api/mail/send');
      const response = await fetch('http://localhost:5000/api/mail/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      console.log('📡 Response status:', response.status);
      const data = await response.json();
      console.log('📥 Response data:', data);

      if (data.success) {
        alert('✅ Message sent successfully! We will get back to you soon.');
        e.target.reset();
      } else {
        // Show validation errors if any
        if (data.errors && data.errors.length > 0) {
          const errorMessages = data.errors.map(err => `• ${err.path}: ${err.msg}`).join('\n');
          alert(`❌ Validation Error:\n\n${errorMessages}`);
          console.error('Validation errors:', data.errors);
        } else {
          alert(data.message || '❌ Failed to send message. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Error details:', error);
      alert(`❌ Network error: ${error.message}\n\nPlease check:\n1. Backend server is running on http://localhost:5000\n2. Open browser console (F12) for details`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center py-20 overflow-hidden font-sans" style={{ fontFamily: 'Rubik, sans-serif' }}>
      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>

      <div className="absolute inset-0 bg-[#f0f4f8] pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-100/40 via-slate-50 to-white"></div>
        <div className="absolute -top-[10%] -left-[10%] w-[500px] h-[500px] bg-blue-300/20 rounded-full blur-[80px]"></div>
        <div className="absolute top-[30%] -right-[5%] w-[400px] h-[400px] bg-purple-300/20 rounded-full blur-[80px]"></div>
        <div className="absolute -bottom-[10%] left-[20%] w-[600px] h-[300px] bg-cyan-200/20 rounded-full blur-[60px]"></div>
      </div>

      <div className="container mx-auto px-10 relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">

          {/* LEFT SIDE */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} className="w-full pt-4 lg:pt-10">
            <motion.div variants={itemVariants} className="mb-12 relative">
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="absolute -top-6 left-0 h-1 w-20 bg-gradient-to-r from-[#2a3890] to-blue-400 rounded-full origin-left"></motion.div>
              <span className="text-[#2a3890] font-bold tracking-[0.2em] uppercase text-sm mb-3 block flex items-center gap-2"><Sparkles size={14} /> Get in Touch</span>
              <h2 className="text-4xl md:text-5xl text-slate-900 font-bold tracking-tight leading-tight">Contact <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2a3890] to-blue-500">Us</span></h2>
              <p className="mt-6 text-lg text-slate-500 max-w-md leading-relaxed">Ready to start your next project? We are here to help you turn your vision into reality.</p>
            </motion.div>

            <div className="space-y-6">
              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 mt-8">
                <a href="https://mail.google.com/mail/?view=cm&to=info@tmgroupdc.com" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-white/60 backdrop-blur-sm border border-white/60 shadow-sm hover:border-[#2a3890] hover:bg-white transition-all group">
                  <Mail className="text-[#2a3890]" size={20} />
                  <span className="text-slate-700 font-medium group-hover:text-[#2a3890]">info@tmgroupdc.com</span>
                </a>
                <a href="tel:+18446335500" className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-white/60 backdrop-blur-sm border border-white/60 shadow-sm hover:border-[#2a3890] hover:bg-white transition-all group">
                  <Phone className="text-[#2a3890]" size={20} />
                  <span className="text-slate-700 font-medium group-hover:text-[#2a3890]"> +1 (202) 445 4459</span>
                </a>
              </motion.div>

              <motion.div variants={itemVariants} className="group relative overflow-hidden flex gap-6 items-start p-6 bg-white/40 backdrop-blur-md border border-white/60 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/30 to-transparent rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110 duration-500"></div>
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shrink-0 shadow-sm text-[#2a3890] group-hover:bg-[#2a3890] group-hover:text-white transition-all duration-300 relative z-10">
                  <MapPin size={26} />
                </div>
                <div className="relative z-10">
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Main Office</h3>
                  <p className="text-slate-600 leading-relaxed font-light">2231 Crystal Drive,<br /> Arlington, VA 22202, USA</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* RIGHT SIDE: FORM */}
          <motion.div variants={formVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-300 to-indigo-300 rounded-[2.5rem] blur-lg opacity-20 pointer-events-none"></div>
            <div className="bg-white/80 backdrop-blur-md p-8 md:p-12 border border-white/50 shadow-lg relative">

              <div className="mb-8 border-b border-slate-200/60 pb-6">
                <h3 className="text-3xl font-bold text-slate-800">Send a Message</h3>
                <p className="text-slate-500 text-sm mt-2 font-light">Fill out the form below and our team will get back to you within 24 hours.</p>
              </div>

              <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
                {/* --- NAME --- */}
                <div className="space-y-2 group">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Full Name</label>
                  <div className="relative group-focus-within:translate-y-[-1px] transition-transform duration-200">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#2a3890] transition-colors" size={20} />
                    <input type="text" name="name" placeholder="John Doe" className="w-full bg-white/50 border border-slate-200/80 pl-12 pr-4 py-4 focus:outline-none focus:border-[#2a3890] focus:bg-white focus:shadow-[0_0_0_4px_rgba(42,56,144,0.1)] transition-all placeholder:text-slate-400 font-light text-slate-700" required />
                  </div>
                </div>

                {/* --- EMAIL --- */}
                <div className="space-y-2 group">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Email Address</label>
                  <div className="relative group-focus-within:translate-y-[-1px] transition-transform duration-200">
                    <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#2a3890] transition-colors" size={20} />
                    <input type="email" name="email" placeholder="john@example.com" className="w-full bg-white/50 border border-slate-200/80 pl-12 pr-4 py-4 focus:outline-none focus:border-[#2a3890] focus:bg-white focus:shadow-[0_0_0_4px_rgba(42,56,144,0.1)] transition-all placeholder:text-slate-400 font-light text-slate-700" required />
                  </div>
                </div>

                {/* --- PHONE & ZIP --- */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* PHONE */}
                  <div className="space-y-2 group relative z-50">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Phone</label>
                    <div className="flex bg-white/50 border border-slate-200/80 focus-within:border-[#2a3890] focus-within:ring-4 focus-within:ring-[#2a3890]/10 focus-within:bg-white transition-all relative">
                      <button type="button" onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center justify-center px-3 bg-slate-50/50 border-r border-slate-200/60 text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
                        <span className="text-lg">{selectedCountry.flag}</span>
                        <ChevronDown size={14} className={`ml-1 opacity-50 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {isDropdownOpen && (
                          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 shadow-xl max-h-60 overflow-y-auto z-[100] no-scrollbar">
                            <div className="py-2">
                              {countries.map((country) => (
                                <button key={country.code} type="button" onClick={() => { setSelectedCountry(country); setIsDropdownOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center gap-3 transition-colors">
                                  <span className="text-xl">{country.flag}</span>
                                  <span className="text-sm text-slate-600 font-medium flex-1">{country.name}</span>
                                  <span className="text-xs text-slate-400 font-mono">{country.dialCode}</span>
                                  {selectedCountry.code === country.code && (<Check size={14} className="text-[#2a3890]" />)}
                                </button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <input type="tel" name="phone" placeholder={`${selectedCountry.dialCode} (202) 445 4459 `} className="w-full bg-transparent p-4 focus:outline-none placeholder:text-slate-400 font-light text-slate-700" required />
                    </div>
                    {isDropdownOpen && <div className="fixed inset-0 z-[90]" onClick={() => setIsDropdownOpen(false)}></div>}
                  </div>

                  {/* ZIP */}
                  <div className="space-y-2 group">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Zip Code</label>
                    <div className="relative group-focus-within:translate-y-[-1px] transition-transform duration-200">
                      <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#2a3890] transition-colors" size={20} />
                      <input type="text" name="zip" placeholder="12345" className="w-full bg-white/50 border border-slate-200/80 pl-12 pr-4 py-4 focus:outline-none focus:border-[#2a3890] focus:bg-white focus:shadow-[0_0_0_4px_rgba(42,56,144,0.1)] transition-all placeholder:text-slate-400 font-light text-slate-700" />
                    </div>
                  </div>
                </div>

                {/* MESSAGE */}
                <div className="space-y-2 group">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Message</label>
                  <div className="relative group-focus-within:translate-y-[-1px] transition-transform duration-200">
                    <MessageSquare className="absolute left-4 top-6 text-slate-400 group-focus-within:text-[#2a3890] transition-colors" size={20} />
                    <textarea name="message" rows="3" placeholder="Tell us about your project requirements..." className="w-full bg-white/50 border border-slate-200/80 pl-12 pr-4 py-4 focus:outline-none focus:border-[#2a3890] focus:bg-white focus:shadow-[0_0_0_4px_rgba(42,56,144,0.1)] transition-all resize-none placeholder:text-slate-400 font-light text-slate-700" required></textarea>
                  </div>
                </div>

                {/* DISCLAIMER */}
                <div className="bg-blue-50/50 p-4 border border-blue-100/50">
                  <p className="text-[11px] text-slate-400 leading-relaxed text-center">
                    By submitting this form, you agree to our privacy policy. We protect your data and never spam.
                  </p>
                </div>

                {/* SUBMIT BUTTON */}
                <motion.button
                  whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#2a3890]'} text-white font-bold py-4 shadow-lg transition-all duration-300 flex items-center justify-center gap-3 text-lg mt-2 relative overflow-hidden group`}
                >
                  <span className="relative z-10">{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  <Send size={20} className={`relative z-10 ${isSubmitting ? '' : 'group-hover:translate-x-1'} transition-transform`} />
                </motion.button>

              </form>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default ContactSection;
